import { useMemo, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  CATEGORIES,
  CHECKLIST,
  PATHWAYS,
  SUB_PATHWAYS_C,
  SCENARIOS,
} from '../data/checklist';
import {
  bucketByCategory,
  countByCategory,
  countItems,
  filterChecklist,
} from '../data/filter';
import { loadSelection } from '../lib/selection';
import type { Category, Selection } from '../data/types';

export function PreviewPage() {
  const navigate = useNavigate();
  const [selection, setSelection] = useState<Selection | null>(null);
  const [activeTab, setActiveTab] = useState<Category>('onboarding');

  useEffect(() => {
    const s = loadSelection();
    if (!s.pathway) {
      navigate('/select', { replace: true });
      return;
    }
    setSelection(s);
  }, [navigate]);

  const filtered = useMemo(
    () => (selection ? filterChecklist(CHECKLIST, selection) : []),
    [selection],
  );
  const buckets = useMemo(() => bucketByCategory(filtered), [filtered]);
  const counts = useMemo(() => countByCategory(filtered), [filtered]);
  const total = countItems(filtered);

  if (!selection) return null;

  const pathwayMeta = PATHWAYS.find((p) => p.id === selection.pathway)!;
  const subPathwayMeta =
    selection.pathway === 'C' && selection.subPathwayC
      ? SUB_PATHWAYS_C.find((sp) => sp.id === selection.subPathwayC)
      : null;
  const scenarioMetas = selection.scenarios
    .map((id) => SCENARIOS.find((s) => s.id === id))
    .filter((s): s is NonNullable<typeof s> => !!s);

  const activeMeta = CATEGORIES.find((c) => c.id === activeTab)!;
  const activeCats = buckets[activeTab];

  return (
    <div className="max-w-4xl mx-auto px-6 py-10 md:py-14">
      {/* Selection summary */}
      <div className="border border-rule p-5 md:p-6 mb-8 bg-paper-deep print:hidden">
        <div className="flex flex-wrap items-baseline justify-between gap-4 mb-4">
          <div>
            <div className="font-label text-[10px] font-semibold uppercase tracking-[0.22em] text-accent mb-1">
              Your filtered checklist
            </div>
            <div className="font-editorial text-[17px] text-ink">
              <strong className="font-medium">{total}</strong> items match your scenario.
            </div>
          </div>
          <Link to="/select" className="btn-text">
            Edit selections
          </Link>
        </div>
        <div className="flex flex-wrap gap-2 text-xs">
          <Badge label={`Pathway ${selection.pathway} — ${pathwayMeta.short}`} />
          {subPathwayMeta && <Badge label={`Sub-pathway ${subPathwayMeta.id}`} />}
          {scenarioMetas.map((s) => (
            <Badge key={s.id} label={`${s.id} — ${s.short}`} />
          ))}
        </div>
      </div>

      {/* Three-tab nav (banner stripes) */}
      <div className="grid grid-cols-3 gap-px bg-rule border border-rule mb-px print:hidden">
        {CATEGORIES.map((c) => {
          const isActive = c.id === activeTab;
          const n = counts[c.id];
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => setActiveTab(c.id)}
              className="px-4 py-3 text-left bg-paper hover:bg-paper-deep transition-colors"
              style={{
                borderBottom: isActive ? `3px solid ${c.accent}` : '3px solid transparent',
              }}
              aria-pressed={isActive}
            >
              <div
                className="font-label text-[10px] font-semibold uppercase tracking-[0.22em] mb-1"
                style={{ color: c.accent }}
              >
                {c.label}
              </div>
              <div className="font-editorial italic text-[13px] text-ink-muted leading-tight">
                {c.tagline}
              </div>
              <div className="font-mono text-[11px] text-ink-soft mt-1.5 tabular-nums">
                {n} {n === 1 ? 'item' : 'items'}
              </div>
            </button>
          );
        })}
      </div>

      {/* Active-tab colored banner */}
      <div
        className="border-l-4 px-5 py-4 mb-8"
        style={{
          background: activeMeta.accentSoft,
          borderColor: activeMeta.accent,
        }}
      >
        <div
          className="font-label text-[10px] font-semibold uppercase tracking-[0.22em] mb-1"
          style={{ color: activeMeta.accent }}
        >
          {activeMeta.label} · {activeMeta.tagline}
        </div>
        <p className="font-editorial text-[14.5px] text-ink-soft leading-relaxed max-w-3xl">
          {activeMeta.description}
        </p>
      </div>

      {/* Read-only preview note */}
      <div className="border-l-2 border-accent pl-6 py-3 mb-10 print:hidden">
        <div className="font-label text-[10px] font-semibold uppercase tracking-[0.22em] text-accent mb-2">
          Read-only preview
        </div>
        <p className="font-editorial italic text-[16px] text-ink-soft leading-relaxed max-w-2xl">
          You're browsing anonymously. Sign-in &amp; persistence — checkbox
          state, notes, owners, due dates, team sharing, PDF/Excel export —
          arrive in the next build phase. For now, you can read the checklist
          and print this page (Cmd-P / Ctrl-P).
        </p>
      </div>

      {/* Active tab content */}
      {activeCats.length === 0 ? (
        <div className="font-editorial italic text-ink-muted py-12 text-center">
          No items in the {activeMeta.label.toLowerCase()} tab match this selection.
        </div>
      ) : (
        activeCats.map((cat) => (
          <section key={cat.id} className="mb-12">
            <h2 className="font-editorial text-[30px] font-medium leading-tight text-ink mb-2 print:text-[22px]">
              <span
                className="italic font-normal mr-3 text-[20px]"
                style={{ color: activeMeta.accent }}
              >
                §{cat.number}
              </span>
              {cat.title}
            </h2>
            {cat.description && (
              <p className="font-editorial italic text-[15px] text-ink-muted mb-6 max-w-3xl leading-relaxed">
                {cat.description}
              </p>
            )}
            {cat.sections.map((sec) => (
              <div key={sec.id} className="mb-7">
                <h3 className="font-editorial text-[20px] font-medium text-ink mb-1 leading-snug">
                  <span
                    className="font-label text-[10px] font-medium uppercase tracking-[0.18em] mr-3 align-middle"
                    style={{ color: activeMeta.accent }}
                  >
                    {sec.number}
                  </span>
                  {sec.title}
                </h3>
                {sec.description && (
                  <p className="font-editorial italic text-[14px] text-ink-muted mb-3 max-w-3xl leading-relaxed">
                    {sec.description}
                  </p>
                )}
                <ul className="border-t border-dotted border-rule">
                  {sec.items.map((it) => (
                    <li
                      key={it.id}
                      className="border-b border-dotted border-rule py-3 flex gap-4 items-start"
                    >
                      <span
                        className="shrink-0 w-4 h-4 mt-1 border border-ink-muted bg-transparent inline-block"
                        aria-hidden="true"
                      />
                      <div className="flex-1">
                        <div className="font-editorial text-[16px] text-ink leading-relaxed">
                          {it.text}
                        </div>
                        {it.note && (
                          <div
                            className="font-editorial italic text-[13px] mt-1"
                            style={{ color: activeMeta.accent }}
                          >
                            ↳ {it.note}
                          </div>
                        )}
                      </div>
                      <span className="font-label text-[10px] text-ink-muted tracking-wider whitespace-nowrap pt-1 print:hidden">
                        {it.id}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </section>
        ))
      )}

      <div className="border-t border-rule pt-8 mt-12 flex items-center justify-between flex-wrap gap-4 print:hidden">
        <Link to="/select" className="btn-text" style={{ borderBottomColor: 'var(--rule)' }}>
          ← Edit selections
        </Link>
        <button type="button" onClick={() => window.print()} className="btn-text">
          Print this page
        </button>
      </div>
    </div>
  );
}

function Badge({ label }: { label: string }) {
  return (
    <span className="font-label text-[10px] font-medium uppercase tracking-[0.15em] text-ink-soft bg-paper border border-rule px-2.5 py-1">
      {label}
    </span>
  );
}
